// apps/web/src/lib/destino-pos-login.ts — Fase 06, Plano 04 (D-01, D-02, D-03).
// Cálculo seguro do destino pós-autenticação e proteção contra Open Redirect (T-6-01 / ASVS V5).
import { destinoInicial } from './destino-inicial';

export const RETORNO_PENDENTE_STORAGE_KEY = 'pieltec_retorno_pendente';

export const ROTAS_CONHECIDAS: readonly string[] = [
  '/painel',
  '/admin/users',
  '/remessas',
  '/producao/nova-op',
  '/coletor/apontamento',
  '/coletor/leitura',
  '/coletor/fila',
  '/qualidade/nao-conformidades',
  '/qualidade/certificados',
  '/estoque',
  '/relatorios/g7',
  '/planejamento/gantt',
  '/planejamento/fila',
  '/cadastros',
  '/cadastros/materiais',
  '/monitoramento/painel',
  '/monitoramento/visao-geral',
  '/relatorios',
  '/omie/clientes',
  '/omie/pendencias',
];

/**
 * Validação de destino por pertencimento estrito à whitelist de rotas internas.
 * Rejeita qualquer tentativa de redirecionamento externo (URLs com protocolo, //, \, etc.)
 * ou rotas inexistentes/inadequadas (/login, /tracer).
 */
export function isDestinoRetornoValido(caminho: string | null | undefined): boolean {
  if (!caminho || typeof caminho !== 'string') {
    return false;
  }
  // Higieniza espaços
  const trimmed = caminho.trim();
  if (trimmed === '/login' || trimmed === '/tracer') {
    return false;
  }
  return ROTAS_CONHECIDAS.includes(trimmed);
}

/**
 * Calcula a rota de destino pós-login com base no perfil e no retorno pendente:
 * - Com as permissões da sessão (caminho normal): quem trabalha no tablet vai sempre para o
 *   destino inicial (D-03); os demais respeitam o retorno pendente válido (D-02) e, sem ele,
 *   vão para `destinoInicial` (Painel, Abastecimento ou primeiro módulo permitido).
 * - Sem permissões (falha ao ler a sessão): regra legada pelo nome do perfil.
 */
export function calcularDestinoPosLogin(
  papel: string | null | undefined,
  retornoPendente?: string | null,
  permissoes?: readonly string[],
): string {
  if (permissoes) {
    const inicial = destinoInicial(permissoes);
    if (inicial.startsWith('/coletor/')) return inicial;
    return isDestinoRetornoValido(retornoPendente) ? (retornoPendente as string) : inicial;
  }

  if (papel === 'Operador') {
    return '/coletor/leitura';
  }

  if (isDestinoRetornoValido(retornoPendente)) {
    return retornoPendente as string;
  }

  return '/painel';
}

/**
 * Persiste apenas o caminho de navegação no sessionStorage (nunca credenciais nem tokens - ADR-003).
 */
export function salvarRetornoPendente(caminho: string): void {
  try {
    if (typeof window !== 'undefined' && isDestinoRetornoValido(caminho)) {
      window.sessionStorage.setItem(RETORNO_PENDENTE_STORAGE_KEY, caminho);
    }
  } catch {
    // sessionStorage indisponível
  }
}

export function obterRetornoPendente(): string | null {
  try {
    if (typeof window !== 'undefined') {
      const salvo = window.sessionStorage.getItem(RETORNO_PENDENTE_STORAGE_KEY);
      return isDestinoRetornoValido(salvo) ? salvo : null;
    }
  } catch {
    // sessionStorage indisponível
  }
  return null;
}

export function limparRetornoPendente(): void {
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(RETORNO_PENDENTE_STORAGE_KEY);
    }
  } catch {
    // sessionStorage indisponível
  }
}
